import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ImportResponse {
  success: boolean;
  data?: {
    eventId: string;
    eventName: string;
    createdGroups: number;
    createdChallenges: number;
    createdGroupUnlocks: number;
    createdArtifacts: number;
  };
  error?: string;
  details?: Array<{ message: string; path?: (string | number)[] }> | string[];
}

interface EventImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal for importing an event from a JSON envelope produced by
 * `GET /events/:id/export`. Handles file selection, parsing, POST
 * to `/events/import`, and (on success) navigates to the freshly
 * created event so admins land on the editable detail page.
 *
 * Validation errors from the API surface the first ~5 issues so
 * the user can fix the source file without bouncing through a
 * truncated 400 body.
 */
export function EventImportDialog({ open, onOpenChange }: EventImportDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const importMutation = useMutation({
    mutationFn: async (selectedFile: File): Promise<ImportResponse> => {
      const text = await selectedFile.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('File is not valid JSON');
      }
      return apiClient.post<ImportResponse>('/events/import', parsed);
    },
    onSuccess: (response) => {
      if (!response.success || !response.data) {
        const messages = (response.details ?? [])
          .map((d) => (typeof d === 'string' ? d : d.message))
          .filter(Boolean);
        setValidationErrors(messages.length > 0 ? messages : [response.error || 'Import failed']);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['Event'] });
      toast.success(`Imported "${response.data.eventName}"`);
      onOpenChange(false);
      setFile(null);
      setValidationErrors([]);
      navigate(`/events/${response.data.eventId}`);
    },
    onError: (err: Error) => {
      setValidationErrors([err.message || 'Import failed']);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setValidationErrors([]);
  };

  const handleImport = () => {
    if (!file) return;
    setValidationErrors([]);
    importMutation.mutate(file);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFile(null);
      setValidationErrors([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Event
          </DialogTitle>
          <DialogDescription>
            Upload a JSON file exported from another event to recreate it in your
            organization. Teams, submissions, and chat history are not imported.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleFileChange}
            className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
          />

          {file && (
            <p className="text-xs text-muted-foreground">
              Selected: <span className="font-medium text-foreground">{file.name}</span> (
              {(file.size / 1024).toFixed(1)} KB)
            </p>
          )}

          {validationErrors.length > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertCircle className="h-4 w-4" />
                Import failed
              </div>
              <ul className="mt-2 space-y-1 text-xs text-destructive">
                {validationErrors.slice(0, 5).map((msg, i) => (
                  <li key={i}>• {msg}</li>
                ))}
                {validationErrors.length > 5 && (
                  <li className="italic">
                    ...and {validationErrors.length - 5} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!file || importMutation.isPending}>
            {importMutation.isPending ? 'Importing...' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
